import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { TextFieldProps } from '../text-field';
import { NumberField } from '../number-field';

interface ControlledNumberFieldProps<TFieldValues extends FieldValues>
  extends Pick<
      TextFieldProps,
      | 'label'
      | 'minRows'
      | 'multiline'
      | 'onChange'
      | 'helperText'
      | 'disabled'
      | 'data-testid'
      | 'labelInfo'
      | 'placeholder'
    >,
    UseControllerProps<TFieldValues> {
  isReadonly?: boolean;
  incrementRate?: number;
  decrementRate?: number;
}

export function ControlledNumberField<TFieldValues extends FieldValues>({
  name,
  control,
  rules,
  label,
  labelInfo,
  multiline,
  minRows,
  placeholder,
  helperText,
  disabled,
  defaultValue,
  isReadonly = false,
  incrementRate = 1,
  decrementRate = 1,
  ...props
}: ControlledNumberFieldProps<TFieldValues>) {
  return (
    <Controller<TFieldValues>
      control={control}
      rules={rules}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        /*
          TODO: we will need to search a patch for those cases to avoid having
          this ts-ignores every time we have to check rules
         */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const maxLength = rules?.maxLength?.value;
        const maxLengthLabel = maxLength
          ? `${value?.length ?? 0}/${maxLength}`
          : undefined;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const min = rules?.min?.value;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const max = rules?.max?.value;

        const handleDecrement = (value: number) => {
          const newValue = Math.max(
            min ?? Number.MIN_SAFE_INTEGER,
            value - decrementRate,
          );
          if (newValue !== value) {
            onChange(newValue);
          }
        };
        const handleIncrement = (value: any) => {
          const newValue = Math.min(
            max ?? Number.MAX_SAFE_INTEGER,
            value + incrementRate,
          );
          if (newValue !== value) {
            onChange(newValue);
          }
        };
        const testId = props['data-testid'] ?? name;

        return (
          <NumberField
            aria-labelledby={`${name}-label`}
            data-testid={testId}
            label={label}
            placeholder={placeholder}
            labelInfo={labelInfo}
            value={value}
            required={!!rules?.required}
            error={!!error?.message}
            helperText={helperText ?? error?.message ?? maxLengthLabel}
            minRows={minRows}
            multiline={multiline}
            inputProps={{
              'data-testid': `${testId}-input`,
              maxLength,
            }}
            InputProps={{
              readOnly: isReadonly,
            }}
            disabled={disabled}
            handleDecrement={handleDecrement}
            handleIncrement={handleIncrement}
          />
        );
      }}
    />
  );
}
